import { defineStore } from 'pinia';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { toFileId, getAttachmentGroupKey } from '@/modules/document/components/file-preview/document-attachment-preview.util.ts';
import { usePreviewFilesStore } from '@/modules/document/stores/preview-files.ts';
import type DocumentFile from '@/modules/document/models/file/document-file.ts';

export type PendingPreviewOpen = {
  fileId: string;
  seq: number;
};

type AttachmentFile = Pick<DocumentFile, 'name'>;

export const usePreviewFilesUiStore = defineStore(StoreId.PreviewFilesUi, {
  state: () => ({
    displayedLength: 1,
    openedOldRevisionIds: [] as string[],
    pendingPreviewOpen: null as PendingPreviewOpen | null,
    previewIconLoadingFileId: null as string | null,
  }),
  actions: {
    requestPreviewOpen(fileId: string | number) {
      const id = toFileId(fileId);
      const seq = (this.pendingPreviewOpen?.seq ?? 0) + 1;

      this.previewIconLoadingFileId = id;
      this.pendingPreviewOpen = { fileId: id, seq };
    },
    finishPreviewOpen(fileId: string, seq: number) {
      if (this.pendingPreviewOpen?.fileId !== fileId || this.pendingPreviewOpen?.seq !== seq) {
        return;
      }

      this.previewIconLoadingFileId = null;
      this.pendingPreviewOpen = null;
    },
    setDisplayedLength(length: number) {
      this.displayedLength = length;
    },
    incrementDisplayedLength() {
      const previewFilesStore = usePreviewFilesStore();

      if (this.displayedLength < previewFilesStore.previewLastRevisionList.length) {
        this.displayedLength++;
      }
    },
    setOpenedOldRevisionIds(ids: (string | number)[]) {
      this.openedOldRevisionIds = ids.map(toFileId);
    },
    closeOldRevisionsForAttachment(attachmentFile: AttachmentFile) {
      const previewFilesStore = usePreviewFilesStore();
      const groupKey = getAttachmentGroupKey(attachmentFile);

      this.openedOldRevisionIds = this.openedOldRevisionIds.filter((id) => {
        const file = previewFilesStore.list.find((item) => toFileId(item.id) === id);

        return !file || getAttachmentGroupKey(file) !== groupKey;
      });
    },
    reset() {
      this.displayedLength = 1;
      this.openedOldRevisionIds = [];
      this.previewIconLoadingFileId = null;
      this.pendingPreviewOpen = null;
    },
  },
});
